import { ReactNode, Component, ReactElement } from 'react';
import _ from "lodash";
import { Property } from "csstype";
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { SettingsList } from '@Primitive/SettingsList';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { ImageViewer as ImageViewerPrimitive, ImageViewerCropperData } from "@Primitive/ImageViewer"
import { DomUtils } from '@Utils/Dom';
import { RamStorage } from '@Utils/RamStorage';
// =======================================================================================================
export interface ImageViewerData {
  /**
   * Default image url
   */
  DefaultImageURL: string;

  /**
   * Whether single view will be used or not.
   * More info about single can be found at ImageViewer primitive
   */
  SingleView: boolean;

  /**
   * Whether to show buttons toolbar or not.
   */
  ShowToolbar: boolean;

  /**
   * Whether to support cropping or not.
   */
  SupportCrop: boolean;

  /**
   * Whether to support image rotation or not.
   */
  SupportRotate: boolean;

  /**
   * Whether to support image drag or not.
   */
  SupportMouseMove: boolean;

  /**
   * The obgject-fit propery of an image.
   */

  ObjectFit: Property.ObjectFit;

  /**
   * Initial cropper's data
   */
  Settings?: ImageViewerCropperData;
}

interface ImageViewerState {
  url: string;
}
// ------------------------------------------------------------------------------------------------------
class ImageViewer extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<ImageViewerData>, any> {
  // ---------------------------------------------------------------------------------------------------
  private StateStorage: RamStorage;
  private settings: ImageViewerCropperData = null;
  // ---------------------------------------------------------------------------------------------------
  constructor(
    props: WidgetsThingsStructs.PartialIWidgetThingsProps<ImageViewerData>
  ) {
    super(props);
    this.StateStorage = RamStorage.GetInstance();
    this.settings = this.StateStorage.GetState(this.props.ThingKey);
    this.state = this.GetStateFromProps(this.props);
    this.onCropperDataChange = this.onCropperDataChange.bind(this);
  }
  // ---------------------------------------------------------------------------------------------------
  private GetStateFromProps(props: WidgetsThingsStructs.PartialIWidgetThingsProps<ImageViewerData>): ImageViewerState {
    let url = props.WidgetData.DefaultImageURL;
    let port = props.PortsStates["img"];
    if (port != null &&
      port.LastStateStatus == StateStatus.Valid &&
      port.State != null &&
      port.State != "") {
      url = port.State;
    }

    return { url: url };
  }
  // ---------------------------------------------------------------------------------------------------
  private onCropperDataChange(data: ImageViewerCropperData) {
    this.settings = data;
    this.StateStorage.SetState(this.props.ThingKey, _.cloneDeep(this.settings));
  }
  // ---------------------------------------------------------------------------------------------------
  public componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<ImageViewerData>) {
    if ((prevProps.PortVersion != this.props.PortVersion) ||
      !_.isEqual(prevProps.WidgetData, this.props.WidgetData))
      this.setState(this.GetStateFromProps(this.props));
  }
  // ---------------------------------------------------------------------------------------------------
  render() {
    return (
      <ImageViewerPrimitive
        width={this.props.width}
        height={this.props.height}
        img={this.state.url}
        cropperData={this.settings}
        onCropperDataChange={this.onCropperDataChange}
        singleView={this.props.WidgetData.SingleView}
        objectFit={this.props.WidgetData.ObjectFit}
        showToolbar={this.props.WidgetData.ShowToolbar}
        // temporarily disable cropping (YCD-289)
        supportCrop={false} //this.props.WidgetData.SupportCrop}
        onSelectedImagePart={(base64Url: string) => {
          //todo: do something
        }}
        supportRotate={this.props.WidgetData.SupportRotate}
        supportMouseMove={this.props.WidgetData.SupportMouseMove}
        localizationKey={this.props.NodeKey}
      />
    );
  }
}

// =======================================================================================================
// Settings

interface ImageViewerWidgetSettingsState {
  WidgetData: ImageViewerData;
}

class ImageViewerWidgetSettings extends Component<
  IWidgetSettingsProps<ImageViewerData>,
  ImageViewerWidgetSettingsState
> {
  constructor(props: IWidgetSettingsProps<ImageViewerData>) {
    super(props);

    this.state = { WidgetData: _.cloneDeep(this.props.WidgetData) };

    // Bind functions
    this.saveData = this.saveData.bind(this);
  }
  // ---------------------------------------------------------------------------------------------------
  saveData() {
    if (isDefined(this.props.onWidgetDataUpdate))
      this.props.onWidgetDataUpdate(this.state.WidgetData);
  }
  // ------------------------------------------------------------------------------------------------------
  render() {
    let settings: SettingsGroup[] = [
      {
        name: "",
        elements: [
          {
            type: SettingsType.ImageFileInput,
            label: "Default Image",
            dataPath: "DefaultImageURL",
            tooltip: 'Image to show when input port has no data.',
            externalURL: true,
            previewImg: true,
            style: { margin: 0 },
            fileInterceptor: (value: string) => {
              return new Promise<string>((resolve, reject) => {
                DomUtils.getResizedImage(value, 1920, 1080,
                  _.isString(value) && value.startsWith('data:image/gif') ? 'gif' : 'jpeg')
                  .then(res => resolve(res.src))
                  .catch(er => reject(er));
              });
            }
          },
          {
            type: SettingsType.Boolean,
            label: "Show toolbar",
            tooltip: 'Show button toolbar on the top of widget or not.',
            dataPath: "ShowToolbar"
          },
          {
            type: SettingsType.Boolean,
            label: "Support image drag",
            tooltip: 'The button toolbar will have a button for toogling drag mode.',
            dataPath: "SupportMouseMove"
          },
          {
            type: SettingsType.Boolean,
            label: "Support rotate",
            tooltip: 'If enabled, two buttons will be shown in the toolbar for rotating clockwise or anti-clockwise the image by 90 degrees.',
            dataPath: "SupportRotate"
          },
          /*
                // temporarily disable cropping
                {
                    type: SettingsType.Boolean,
                    label: "SupportCrop",
                    dataPath: "Support crop",
                },
                */
          {
            type: SettingsType.Boolean,
            label: "Single view mode",
            tooltip: "If enabled, no toolbar will be shown and the image will fill its content box maintining image's aspect ratio.",
            dataPath: "SingleView"
          },
          {
            type: SettingsType.DropDown,
            label: "Resize Policy",
            tooltip: "If SingleMode is enabled you can define the image resize policy. Available options: `Fill`, `Contain`, `Cover`, `Scale-down`, `None`.",
            dataPath: "ObjectFit",
            items: [
              { value: "fill", label: "Fill" },
              { value: "contain", label: "Contain" },
              { value: "cover", label: "Cover" },
              { value: "scale-down", label: "Scale-down" },
              { value: "none", label: "None" }
            ],
            isHidden: (d) => !d.SingleView
          }
        ]
      }
    ];

    return (
      <SettingsList
        AutoSave={true}
        SettingsMap={settings}
        Data={this.state.WidgetData}
        onSaveData={data =>
          this.setState({ WidgetData: data }, () => this.saveData())
        }
        Toolbar={this.props.SettingListToolbar}
      />
    );
  }
  // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
// Description interface

export class ImageViewerWidget extends IWidgetThingsClass<ImageViewerData> {
  // ------------------------------------------------------------------------------------------------
  public Render(key: string, width: number, height: number): ReactNode {
    return <ImageViewer />;
  }
  // ------------------------------------------------------------------------------------------------
  public RenderWidgetSettings(
    key: string,
    width: number,
    height: number
  ): ReactElement<any> {
    const parent = this.Parent;
    return <ImageViewerWidgetSettings
      key={"Settings-" + key}
      width={width}
      height={height}
      WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
      WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
      onWidgetDataUpdate={d => this.WidgetDataUpdate(d)}
      ThingKey={this.ThingKey}
      NodeKey={this.NodeKey}
      SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
    />;
  }
  // ------------------------------------------------------------------------------------------------
  initThing() {
    // Thing description
    let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

    // Ports
    let ports: WebThingPort[] = [];
    ports.push(
      new WebThingPort(
        {
          Name: "Image",
          PortKey: "img",
          Type: Plegma.ePortType.String,
          ioDirection: Plegma.ioPortDirection.Input,
          State: ""
        },
        WebThingPortType.Data
      )
    );

    // Create the thing
    let thing = new WebThing(this.WebModule, plegmaThing, ports);
    this.ThingKey = thing.ThingKey;
  }
  // ------------------------------------------------------------------------------------------------
  public static GetDefaultWidgetDataStatic(): ImageViewerData {
    return {
      DefaultImageURL: "",
      SupportCrop: false,
      SupportMouseMove: false,
      ShowToolbar: true,
      SingleView: false,
      ObjectFit: "cover",
      SupportRotate: false
    };
  }
  // ------------------------------------------------------------------------------------------------
  public GetDefaultWidgetData() {
    return ImageViewerWidget.GetDefaultWidgetDataStatic();
  }
  // ------------------------------------------------------------------------------------------------
  public static GetWidgetDesc(): IWidgetDescription {
    let data: ImageViewerData = ImageViewerWidget.GetDefaultWidgetDataStatic();
    return {
      Title: "Image Viewer",
      WidgetTitle: "",
      Key: "ImageViewerWidget",
      Type: "ImageViewerWidget",
      Data: data,
      MinH: 1,
      MinW: 1,
      ShowExtraMenu: false,
      IconURI: WidgetsThingsStructs.IconURIPath + "/png/imageviewer.png"
    };
  }
  // ------------------------------------------------------------------------------------------------
  public GetLocalizationDictionary() {
    if (!IsNullOrEmpty(this.Description.WidgetTitle))
      return {
        [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
      };
    else
      // nothing to expose
      return {};
  }
  // ------------------------------------------------------------------------------------------------
  GetDataPortKeyStatic(): string { return "img"; };
  // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================